import { behaviorTimerClient } from "../behavior-timers/client.js";
import {
  timerScope,
  type BehaviorTimerData,
} from "../behavior-timers/protocol.js";
import type { BehaviorGraph, OrganisationData } from "../layout/types.js";
import { resolveCustomDisciplineOrganisation } from "./cell-runtime-data.js";
import type { PrimitiveNode } from "../layout/types.js";
import { resolveBehaviorFlowStateKey } from "../layout-compiler/flowState.js";
import { compileTextNode } from "../layout-compiler/compileTextNode.js";
import type {
  BehaviorFlowExecutionState,
  CompiledTextNode,
  RenderCacheEntry,
  RuntimeContext,
  RuntimePatch,
  RuntimeEffect,
  SceneCatalogDependencyIndex,
} from "../layout-compiler/types.js";
import {
  CUSTOM_DISCIPLINE_AGE_CATEGORIES_SOURCE,
  CUSTOM_DISCIPLINE_SPORT_AGE_CATEGORIES_SOURCE,
  CUSTOM_DISCIPLINE_SPORT_WEIGHT_CATEGORIES_SOURCE,
  CUSTOM_DISCIPLINE_ATHLETE_SOURCE,
  CUSTOM_DISCIPLINE_ATHLETES_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_GROUP_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_MEET_SOURCE,
  CUSTOM_DISCIPLINE_CURRENT_VALUE_SOURCE,
  CUSTOM_DISCIPLINE_DISCIPLINES_SOURCE,
  CUSTOM_DISCIPLINE_GROUP_SOURCE,
  CUSTOM_DISCIPLINE_GROUPS_SOURCE,
  CUSTOM_DISCIPLINE_INPUT_VALUES_SOURCE,
  CUSTOM_DISCIPLINE_LEAGUES_SOURCE,
  CUSTOM_DISCIPLINE_ORGANISATIONS_SOURCE,
  CUSTOM_DISCIPLINE_ORGANISATION_SOURCE,
  CUSTOM_DISCIPLINE_LEVELS_SOURCE,
  CUSTOM_DISCIPLINE_MODAL_VALUES_SOURCE,
  CUSTOM_DISCIPLINE_RECORDS_SOURCE,
  CUSTOM_DISCIPLINE_REFERENCE_ITEM_SOURCE,
  CUSTOM_DISCIPLINE_REFEREE_LIGHTS_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE,
  CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE,
  CUSTOM_DISCIPLINE_WEIGHT_CATEGORIES_SOURCE,
} from "./sources.js";
import type { CustomDisciplineAthleteRecord } from "./records.js";

const EMPTY_CATALOG_DEPS: SceneCatalogDependencyIndex = {
  variableDependenciesById: {},
  functionDependenciesById: {},
};

const flowStateByNodeKey = new Map<string, BehaviorFlowExecutionState>();
const renderCacheByNodeKey = new Map<string, RenderCacheEntry>();
const runtimePatchStateByNodeKey = new Map<string, RuntimePatch[]>();

/**
 * Compiled custom-discipline field graph, wrapped as a text primitive.
 */
export type CompiledCustomDisciplineField = {
  fieldId: string;
  compiled: CompiledTextNode;
};

export type { CustomDisciplineAthleteRecord } from "./records.js";

export type CustomDisciplineEvaluateInput = {
  /** Internal pure-expression cache, shared only inside one immutable batch. */
  evaluationCache?: RuntimeContext["__expressionCache"];
  behaviorTimers?: BehaviorTimerData;
  behaviorTimerScope?: string;
  athlete?: CustomDisciplineAthleteRecord;
  athletes: CustomDisciplineAthleteRecord[];
  currentValue?: string | number | boolean | null;
  defaultText: string;
  instanceKey: string;
  selectedRowId?: string;
  selectedColumnKey?: string;
  currentMeet?: unknown;
  displaySettings?: import("../layout/types.js").InjectedData["displaySettings"];
  /** Context group (opened page / evaluated meet row). */
  group?: unknown;
  /** Meet-selected current group (`meetStore.currentGroup`). */
  currentGroup?: unknown;
  groups?: unknown[];
  records?: unknown[];
  levels?: unknown[];
  ageCategories?: unknown[];
  disciplineAgeCategories?: unknown[];
  disciplineWeightCategories?: unknown[];
  weightCategories?: unknown[];
  disciplines?: unknown[];
  leagues?: unknown[];
  organisations?: unknown[];
  organisation?: OrganisationData | null;
  modalValues?: Record<string, unknown>;
  inputValues?: Record<string, unknown>;
  /** Raw automatic-refereeing event; sport rules stay in the Behavior graph. */
  refereeLights?: unknown;
  controlRoomDispatches?: import("../layout-compiler/types.js").RuntimeControlRoomDispatch[];
  shortcutPreferences?: readonly import("../blueprint/shortcut-keys.js").ShortcutPreference[];
  /** Candidate reference-list item, only used by `referenceFilterBehavior` graphs. */
  referenceItem?: unknown;
};

export type CustomDisciplineCellResult = {
  text: string;
  style: Record<string, unknown>;
};

export type CustomDisciplineActionVisualOverride = {
  label?: string;
  tooltip?: string;
  iconSize?: number;
  iconStyle?: Record<string, unknown>;
  isDisabled?: boolean;
  isVisible?: boolean;
  startIcon?: string | null;
  variant?: string;
  size?: string;
  color?: string;
};

export type CustomDisciplineListSetFieldPayload = {
  list: unknown;
  searchKey: unknown;
  searchValue: unknown;
  fieldKey: unknown;
  nextValue: unknown;
};

export type CustomDisciplineActionEvaluateResult = {
  visual: CustomDisciplineActionVisualOverride;
  /** One-shot commands in graph execution order; never replay stored patches. */
  commands: Extract<RuntimeEffect, { type: "customDiscipline.action" }>[];
  /** All list-field mutations from the click flow, in execution order. */
  listSetFields?: CustomDisciplineListSetFieldPayload[];
  /** @deprecated Prefer {@link CustomDisciplineActionEvaluateResult.listSetFields}. */
  listSetField?: CustomDisciplineListSetFieldPayload;
  listSetRows?: {
    list: unknown;
    mode: unknown;
    targetGroupId: unknown;
    persistFields: unknown;
    deleteMissing: unknown;
  };
  listSetViewRows?: {
    list: unknown;
    includeNewRows?: unknown;
  };
  groupSetField?: {
    groupId: unknown;
    fieldKey: unknown;
    nextValue: unknown;
  };
  openGroup?: {
    groupId: unknown;
  };
  setCurrentGroup?: {
    groupId: unknown;
  };
  setCurrentAthlete?: {
    athleteId: unknown;
  };
  openModal?: {
    modalKey: unknown;
    contextAthleteId: unknown;
    contextGroupId: unknown;
  };
  closeModal?: {
    modalKey: unknown;
  };
};

/**
 * Clears per-cell onLoad / render-cache state.
 *
 * Call when the schema is re-synced so graphs re-run `onLoad` with a fresh
 * flow map. Virtualized DataGrid cells keep their instance keys otherwise.
 */
export function resetCustomDisciplineBehaviorRuntime(): void {
  flowStateByNodeKey.clear();
  renderCacheByNodeKey.clear();
  runtimePatchStateByNodeKey.clear();
}

/** Release a closed host/modal without resetting other open groups or displays. */
export function releaseCustomDisciplineBehaviorRuntime(
  instancePrefix: string,
): void {
  for (const map of [
    flowStateByNodeKey,
    renderCacheByNodeKey,
    runtimePatchStateByNodeKey,
  ])
    for (const key of map.keys())
      if (key.startsWith(instancePrefix)) map.delete(key);
}

/**
 * Compiles a custom-discipline field or action graph into the layout text pipeline.
 *
 * Wraps the graph on a dummy `primitive: "text"` node so `setBinding` writes
 * `text` (same as {@link compileTextNode} / `applyPatchToTextNode`).
 *
 * @param fieldId - Stable field or action definition id (patch owner + compile node id).
 * @param graph - Parsed behavior graph.
 * @returns Compiled evaluator reused across DataGrid cells and action buttons.
 */
export function compileCustomDisciplineField(
  fieldId: string,
  graph: BehaviorGraph,
): CompiledCustomDisciplineField {
  const node: PrimitiveNode = {
    id: fieldId,
    kind: "primitive",
    primitive: "text",
    x: 0,
    y: 0,
    width: 1,
    height: 1,
    zIndex: 0,
    text: "",
    behavior: graph,
  };
  return {
    fieldId,
    compiled: compileTextNode(node, { catalogDeps: EMPTY_CATALOG_DEPS }),
  };
}

function resolveCustomDisciplineFlowStateKey(
  ownerNodeId: string,
  input: CustomDisciplineEvaluateInput,
): string {
  return resolveBehaviorFlowStateKey(ownerNodeId, {
    __nodeInstanceKey: input.instanceKey,
  } as RuntimeContext);
}

function emptyRuntimeContext(
  input: CustomDisciplineEvaluateInput,
  ownerNodeId: string,
  options?: { click?: boolean; runtimeEffects?: boolean; isolated?: boolean },
): RuntimeContext {
  const flowStateKey = resolveCustomDisciplineFlowStateKey(ownerNodeId, input);
  const ctx = {
    behaviorTimers:
      input.behaviorTimers ?? behaviorTimerClient.data(timerScope(input), true),
    behaviorTimerScope: timerScope(input),
    __nodeInstanceKey: input.instanceKey,
    __expressionCache: input.evaluationCache ?? { byContext: new WeakMap() },
    __signatureValueCache: new WeakMap<object, string>(),
    __flowStateByNodeKey: options?.isolated ? new Map() : flowStateByNodeKey,
    __renderCacheByNodeKey: options?.isolated
      ? new Map()
      : renderCacheByNodeKey,
    __runtimePatchStateByNodeKey: options?.isolated
      ? new Map()
      : runtimePatchStateByNodeKey,
    __runtimeEffects: [],
    __runtimeContinuations: [],
    __controlRoomDispatches: input.controlRoomDispatches ?? [],
    __shortcutPreferences: input.shortcutPreferences,
    __suppressRuntimeEffects: options?.runtimeEffects !== true,
    __customDisciplineClick: options?.click === true,
    [CUSTOM_DISCIPLINE_ATHLETE_SOURCE]: input.athlete,
    [CUSTOM_DISCIPLINE_ATHLETES_SOURCE]: input.athletes,
    [CUSTOM_DISCIPLINE_CURRENT_VALUE_SOURCE]:
      input.currentValue ?? input.defaultText,
    [CUSTOM_DISCIPLINE_SELECTED_ROW_ID_SOURCE]: input.selectedRowId ?? "",
    [CUSTOM_DISCIPLINE_SELECTED_COLUMN_KEY_SOURCE]:
      input.selectedColumnKey ?? "",
    [CUSTOM_DISCIPLINE_CURRENT_MEET_SOURCE]: input.currentMeet,
    [CUSTOM_DISCIPLINE_ORGANISATION_SOURCE]:
      resolveCustomDisciplineOrganisation(input),
    displaySettings: input.displaySettings,
    [CUSTOM_DISCIPLINE_GROUP_SOURCE]: input.group,
    [CUSTOM_DISCIPLINE_CURRENT_GROUP_SOURCE]: input.currentGroup,
    [CUSTOM_DISCIPLINE_GROUPS_SOURCE]: input.groups ?? [],
    [CUSTOM_DISCIPLINE_RECORDS_SOURCE]: input.records ?? [],
    [CUSTOM_DISCIPLINE_LEVELS_SOURCE]: input.levels ?? [],
    [CUSTOM_DISCIPLINE_AGE_CATEGORIES_SOURCE]: input.ageCategories ?? [],
    [CUSTOM_DISCIPLINE_SPORT_AGE_CATEGORIES_SOURCE]:
      input.disciplineAgeCategories ?? [],
    [CUSTOM_DISCIPLINE_SPORT_WEIGHT_CATEGORIES_SOURCE]:
      input.disciplineWeightCategories ?? [],
    [CUSTOM_DISCIPLINE_WEIGHT_CATEGORIES_SOURCE]: input.weightCategories ?? [],
    [CUSTOM_DISCIPLINE_DISCIPLINES_SOURCE]: input.disciplines ?? [],
    [CUSTOM_DISCIPLINE_LEAGUES_SOURCE]: input.leagues ?? [],
    [CUSTOM_DISCIPLINE_ORGANISATIONS_SOURCE]: input.organisations ?? [],
    [CUSTOM_DISCIPLINE_MODAL_VALUES_SOURCE]: input.modalValues ?? {},
    [CUSTOM_DISCIPLINE_INPUT_VALUES_SOURCE]: input.inputValues ?? {},
    [CUSTOM_DISCIPLINE_REFERENCE_ITEM_SOURCE]: input.referenceItem,
    [CUSTOM_DISCIPLINE_REFEREE_LIGHTS_SOURCE]: input.refereeLights,
  } as unknown as RuntimeContext;
  if (options?.click === true) {
    renderCacheByNodeKey.delete(flowStateKey);
  }
  return ctx;
}

type CustomDisciplineEvaluateSnapshot = {
  patches: ReadonlyArray<{ key: string; value: unknown }>;
  effects: ReadonlyArray<{ type: string; action?: string; payload?: unknown }>;
};

function readEvaluateSnapshot(
  compiled: CompiledCustomDisciplineField,
  input: CustomDisciplineEvaluateInput,
  options?: { click?: boolean; runtimeEffects?: boolean; isolated?: boolean },
): CustomDisciplineEvaluateSnapshot {
  const flowStateKey = resolveCustomDisciplineFlowStateKey(
    compiled.fieldId,
    input,
  );
  const ctx = emptyRuntimeContext(input, compiled.fieldId, options);
  try {
    compiled.compiled.evaluate(ctx);
  } catch {
    return { patches: [], effects: [] };
  }
  return {
    patches: ctx.__runtimePatchStateByNodeKey?.get(flowStateKey) ?? [],
    effects: ctx.__runtimeEffects ?? [],
  };
}

function parseListSetFieldPayload(
  value: unknown,
): CustomDisciplineListSetFieldPayload | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const payload = value as {
    list?: unknown;
    searchKey?: unknown;
    searchValue?: unknown;
    fieldKey?: unknown;
    nextValue?: unknown;
  };
  return {
    list: payload.list,
    searchKey: payload.searchKey,
    searchValue: payload.searchValue,
    fieldKey: payload.fieldKey,
    nextValue: payload.nextValue,
  };
}

function listSetFieldsFromEffects(
  effects: CustomDisciplineEvaluateSnapshot["effects"],
): CustomDisciplineListSetFieldPayload[] {
  return effects.flatMap((effect) => {
    if (
      effect.type !== "customDiscipline.action" ||
      effect.action !== "customListSetField"
    ) {
      return [];
    }
    const parsed = parseListSetFieldPayload(effect.payload);
    return parsed ? [parsed] : [];
  });
}

function listSetFieldsFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): CustomDisciplineListSetFieldPayload[] {
  return patches.flatMap((patch) => {
    if (patch.key !== "customListSetField") {
      return [];
    }
    const parsed = parseListSetFieldPayload(patch.value);
    return parsed ? [parsed] : [];
  });
}

function visualOverrideFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): CustomDisciplineActionVisualOverride {
  const visual: CustomDisciplineActionVisualOverride = {};
  for (const patch of patches) {
    switch (patch.key) {
      case "iconSize":
        visual.iconSize = Number(patch.value);
        break;
      case "opacity":
      case "filter":
        visual.iconStyle = { ...visual.iconStyle, [patch.key]: patch.value };
        break;
      case "visibility":
        visual.isVisible = Boolean(patch.value);
        break;
      case "customActionLabel":
        visual.label = String(patch.value ?? "");
        break;
      case "customActionTooltip":
        visual.tooltip = String(patch.value ?? "");
        break;
      case "customActionDisabled":
        visual.isDisabled = Boolean(patch.value);
        break;
      case "customActionIcon":
      case "customActionStartIcon":
        visual.startIcon = patch.value == null ? null : String(patch.value);
        break;
      case "customActionVariant":
        visual.variant = String(patch.value ?? "");
        break;
      case "customActionSize":
        visual.size = String(patch.value ?? "");
        break;
      case "customActionColor":
        visual.color = String(patch.value ?? "");
        break;
      default:
        break;
    }
  }
  return visual;
}

function resolveListSetFieldsFromSnapshot(
  snapshot: CustomDisciplineEvaluateSnapshot,
): CustomDisciplineListSetFieldPayload[] {
  const fromEffects = listSetFieldsFromEffects(snapshot.effects);
  if (fromEffects.length > 0) {
    return fromEffects;
  }
  return listSetFieldsFromPatches(snapshot.patches);
}

function listSetRowsFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): CustomDisciplineActionEvaluateResult["listSetRows"] {
  const patch = patches.find((item) => item.key === "customListSetRows");
  if (!patch || !patch.value || typeof patch.value !== "object") {
    return undefined;
  }
  const value = patch.value as {
    list?: unknown;
    mode?: unknown;
    targetGroupId?: unknown;
    persistFields?: unknown;
    deleteMissing?: unknown;
  };
  return {
    list: value.list,
    mode: value.mode,
    targetGroupId: value.targetGroupId,
    persistFields: value.persistFields,
    deleteMissing: value.deleteMissing,
  };
}

function listSetViewRowsFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): CustomDisciplineActionEvaluateResult["listSetViewRows"] {
  const patch = patches.find((item) => item.key === "customListSetViewRows");
  if (!patch || !patch.value || typeof patch.value !== "object") {
    return undefined;
  }
  return {
    list: (patch.value as { list?: unknown }).list,
    includeNewRows: (patch.value as { includeNewRows?: unknown })
      .includeNewRows,
  };
}

function groupSetFieldFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): CustomDisciplineActionEvaluateResult["groupSetField"] {
  const patch = patches.find((item) => item.key === "customGroupSetField");
  if (!patch || !patch.value || typeof patch.value !== "object") {
    return undefined;
  }
  const value = patch.value as {
    groupId?: unknown;
    fieldKey?: unknown;
    nextValue?: unknown;
  };
  return {
    groupId: value.groupId,
    fieldKey: value.fieldKey,
    nextValue: value.nextValue,
  };
}

function openGroupFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): CustomDisciplineActionEvaluateResult["openGroup"] {
  const patch = patches.find((item) => item.key === "customOpenGroup");
  if (!patch || !patch.value || typeof patch.value !== "object") {
    return undefined;
  }
  const groupId = String(
    (patch.value as { groupId?: unknown }).groupId ?? "",
  ).trim();
  if (!groupId) {
    return undefined;
  }
  return { groupId };
}

function setCurrentGroupFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): CustomDisciplineActionEvaluateResult["setCurrentGroup"] {
  const patch = patches.find((item) => item.key === "customSetCurrentGroup");
  if (!patch || !patch.value || typeof patch.value !== "object") {
    return undefined;
  }
  const groupId = String(
    (patch.value as { groupId?: unknown }).groupId ?? "",
  ).trim();
  return { groupId };
}

function setCurrentAthleteFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): CustomDisciplineActionEvaluateResult["setCurrentAthlete"] {
  const patch = patches.find((item) => item.key === "customSetCurrentAthlete");
  if (!patch || !patch.value || typeof patch.value !== "object") {
    return undefined;
  }
  const athleteId = String(
    (patch.value as { athleteId?: unknown }).athleteId ?? "",
  ).trim();
  return { athleteId };
}

function referenceFilterFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): boolean {
  const patch = patches.find((item) => item.key === "customReferenceFilter");
  if (!patch) {
    return true;
  }
  return Boolean(patch.value);
}

function openModalFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): CustomDisciplineActionEvaluateResult["openModal"] {
  const patch = patches.find((item) => item.key === "customOpenModal");
  if (!patch || !patch.value || typeof patch.value !== "object") {
    return undefined;
  }
  const value = patch.value as {
    modalKey?: unknown;
    contextAthleteId?: unknown;
    contextGroupId?: unknown;
  };
  return {
    modalKey: value.modalKey,
    contextAthleteId: value.contextAthleteId,
    contextGroupId: value.contextGroupId,
  };
}

function closeModalFromPatches(
  patches: ReadonlyArray<{ key: string; value: unknown }>,
): CustomDisciplineActionEvaluateResult["closeModal"] {
  const patch = patches.find((item) => item.key === "customCloseModal");
  if (!patch || !patch.value || typeof patch.value !== "object") {
    return undefined;
  }
  const value = patch.value as { modalKey?: unknown };
  const modalKey = String(value.modalKey ?? "").trim();
  if (!modalKey) {
    return undefined;
  }
  return { modalKey };
}

/**
 * Evaluates a compiled field for one DataGrid cell.
 *
 * `onLoad` flow state is keyed by `{instanceKey}:{fieldId}` in
 * module-level maps so virtualization does not re-fire load continuations.
 * Bindings re-evaluate when `athlete` / `athletes` change (render cache).
 *
 * Without a `setBinding` (Source) patch, `text` falls back to `defaultText`.
 * GROUP grids may omit `athlete`; graphs that read it keep `defaultText`.
 *
 * @param compiled - Output of {@link compileCustomDisciplineField}.
 * @param input - Row records, fallback label, and stable instance key.
 * @returns Display text plus CSS-like style patches.
 */
export function evaluateCustomDisciplineCell(
  compiled: CompiledCustomDisciplineField,
  input: CustomDisciplineEvaluateInput,
): CustomDisciplineCellResult {
  const flowStateKey = resolveCustomDisciplineFlowStateKey(
    compiled.fieldId,
    input,
  );
  const ctx = emptyRuntimeContext(input, compiled.fieldId);
  let rendered: ReturnType<CompiledTextNode["evaluate"]> = null;
  try {
    rendered = compiled.compiled.evaluate(ctx);
  } catch {
    return { text: input.defaultText, style: {} };
  }

  const patches = ctx.__runtimePatchStateByNodeKey?.get(flowStateKey) ?? [];
  const style: Record<string, unknown> = {
    ...(rendered?.style ?? {}),
  };
  for (const patch of patches) {
    if (
      patch.key === "setBinding" ||
      patch.key === "location" ||
      patch.key === "transform"
    ) {
      continue;
    }
    style[patch.key] = patch.value;
  }

  const bindingPatch = patches.find((patch) => patch.key === "setBinding");
  const boundText = bindingPatch?.value;
  const hasBoundText = boundText != null && String(boundText).trim() !== "";
  const renderedText =
    rendered?.text != null && String(rendered.text).trim() !== ""
      ? String(rendered.text)
      : "";
  const text = hasBoundText
    ? String(boundText)
    : renderedText || input.defaultText;

  if (rendered == null) {
    const visibilityPatch = patches.find((patch) => patch.key === "visibility");
    if (
      visibilityPatch?.value === false ||
      visibilityPatch?.value === "hidden"
    ) {
      style.visibility = false;
    }
  }

  return { text, style };
}

/**
 * Evaluates a compiled action graph for visual button overrides.
 *
 * Runs `onLoad` (and persisted patches) like cells. `onClick` is not fired.
 *
 * @param compiled - Output of {@link compileCustomDisciplineField} for an action.
 * @param input - Shared evaluate input (`athletes`, selected cell, instance key).
 * @returns Visual overrides from `customAction*` patches.
 */
export function evaluateCustomDisciplineAction(
  compiled: CompiledCustomDisciplineField,
  input: CustomDisciplineEvaluateInput,
): CustomDisciplineActionEvaluateResult {
  const { patches, effects } = readEvaluateSnapshot(compiled, input);
  return {
    visual: visualOverrideFromPatches(patches),
    commands: effects.filter(
      (effect) => effect.type === "customDiscipline.action",
    ) as Extract<RuntimeEffect, { type: "customDiscipline.action" }>[],
  };
}

/** Dispatches an external event without allowing ordinary grid rendering to mutate data. */
export function evaluateCustomDisciplineEvent(
  compiled: CompiledCustomDisciplineField,
  input: CustomDisciplineEvaluateInput,
): CustomDisciplineActionEvaluateResult {
  const { patches, effects } = readEvaluateSnapshot(compiled, input, {
    runtimeEffects: true,
  });
  return {
    visual: visualOverrideFromPatches(patches),
    commands: effects.filter(
      (effect) => effect.type === "customDiscipline.action",
    ) as Extract<RuntimeEffect, { type: "customDiscipline.action" }>[],
  };
}

/**
 * Evaluates a compiled action graph for an explicit button click.
 *
 * Runs `onClick` in addition to `onLoad` so mutations (`customListSetField`)
 * execute once per click, while visual patches remain available.
 *
 * @param compiled - Output of {@link compileCustomDisciplineField} for an action.
 * @param input - Shared evaluate input at click time.
 * @returns Visual overrides plus an optional list-field mutation payload.
 */
export function evaluateCustomDisciplineActionClick(
  compiled: CompiledCustomDisciplineField,
  input: CustomDisciplineEvaluateInput,
): CustomDisciplineActionEvaluateResult {
  const snapshot = readEvaluateSnapshot(compiled, input, { click: true });
  const listSetFields = resolveListSetFieldsFromSnapshot(snapshot);
  return {
    visual: visualOverrideFromPatches(snapshot.patches),
    commands: snapshot.effects.filter(
      (effect) => effect.type === "customDiscipline.action",
    ) as Extract<RuntimeEffect, { type: "customDiscipline.action" }>[],
    listSetFields,
    listSetField: listSetFields[0],
    listSetRows: listSetRowsFromPatches(snapshot.patches),
    listSetViewRows: listSetViewRowsFromPatches(snapshot.patches),
    groupSetField: groupSetFieldFromPatches(snapshot.patches),
    openGroup: openGroupFromPatches(snapshot.patches),
    setCurrentGroup: setCurrentGroupFromPatches(snapshot.patches),
    setCurrentAthlete: setCurrentAthleteFromPatches(snapshot.patches),
    openModal: openModalFromPatches(snapshot.patches),
    closeModal: closeModalFromPatches(snapshot.patches),
  };
}

/**
 * Evaluates a compiled `referenceFilterBehavior` graph for one candidate
 * reference-list item (e.g. one `ageCategories` row) to decide whether it
 * should remain selectable as a foreign-key option.
 *
 * Runs `onLoad` only (no click). `input.referenceItem` is the candidate item
 * and `input.instanceKey` must be unique per item (e.g. `fieldId:itemId`) so
 * render-cache / flow state do not leak between items.
 *
 * @param compiled - Output of {@link compileCustomDisciplineField} for the field's `referenceFilterBehavior`.
 * @param input - Shared evaluate input plus the candidate `referenceItem`.
 * @returns `true` if the item should be kept (default when no `customReferenceFilter` action ran).
 */
export function evaluateCustomDisciplineReferenceFilter(
  compiled: CompiledCustomDisciplineField,
  input: CustomDisciplineEvaluateInput,
): boolean {
  // A candidate filter is a predicate, not a long-lived mounted cell. Keeping
  // per-option flow signatures retained whole reference lists across forms.
  const { patches } = readEvaluateSnapshot(compiled, input, { isolated: true });
  return referenceFilterFromPatches(patches);
}

/** Pure ticket policy evaluation: no business commands or retained runtime state. */
export function evaluateCustomDisciplinePolicyPatches(
  compiled: CompiledCustomDisciplineField,
  input: CustomDisciplineEvaluateInput,
) {
  return readEvaluateSnapshot(compiled, input, { isolated: true }).patches;
}
